import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from '../components/ui/Avatar';
import ImageCropPopup from '../components/popups/ImageCropPopup';
import { ProfileContext } from '../contexts/ProfileContext';
import { toast } from 'react-toastify';
import { getUploadingFileData, postUserImageLink, uploadFileToAWS } from '../service';
import { useTranslation } from 'react-i18next';
import { getTranslation } from '../utils';
import { CaretLeftIcon } from '@phosphor-icons/react';
import Button from '../components/ui/button';
import { Spinner } from '../components/common';
import { checkRequiredFieldsInformationForm } from '../utils/utils';

const AWS_URL = process.env.REACT_APP_AWS_URL;

export default function PgProfileImage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const imgRef = useRef();
  const cropperRef = useRef();
  const [profile, setProfile] = useContext(ProfileContext);
  const [avatar, setAvatar] = useState('');
  const [croppedImg, setCroppedImg] = useState(null);
  const [croppedImageBlob, setCroppedImageBlob] = useState(null);
  const [showImgCropper, setShowImgCropper] = useState(false);
  const [loading, setLoading] = useState(false);

  const { masterData: { translationPrefix, customConfigurations: { colors } = {} } = {} } = profile;

  useEffect(() => {
    if (profile?.loaded && !profile?.image) {
      setAvatar('./images/avatar.png');
      return;
    } else {
      setAvatar(AWS_URL + 'profile-images/' + profile.image);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.loaded]);

  const buttonHandler = async () => {
    setLoading(true);

    try {
      let imgBlob;

      // If cropper is active, get the cropped image first
      if (showImgCropper) {
        if (cropperRef.current) {
          imgBlob = await cropperRef.current.getCropDataAsBlob();
          setShowImgCropper(false);
        } else {
          setLoading(false);
          return;
        }
      } else if (croppedImageBlob) {
        imgBlob = croppedImageBlob;
      } else {
        // If no new image, just go back
        navigate('/my-profile');
        setLoading(false);
        return;
      }

      const body = {
        type: imgBlob.type,
        name: 'img.png',
        isProfileImage: true
      };

      const {
        data: { url, finalName }
      } = await getUploadingFileData(body);
      await uploadFileToAWS(url, imgBlob, body.type);
      await postUserImageLink({
        image: finalName
      });
      setCroppedImageBlob(null);
      setCroppedImg(null);
      const updatedProfile = {
        ...profile,
        image: finalName,
        isUpdatedForPDF: true,
        validationReduested: false
      };
      setProfile(updatedProfile);

      const isInformationsComplete = checkRequiredFieldsInformationForm(profile)?.length === 0;

      if (!isInformationsComplete) {
        navigate('/profile-data');
      } else if (!profile.strengths || profile.strengths.length === 0) {
        navigate('/strengths');
      } else if (!profile.roles || profile.roles.length === 0) {
        navigate('/roles');
      } else {
        navigate('/my-profile');
      }
    } catch (error) {
      console.log('Upload Error', error);
      toast.error('Error with uploading the profile picture!');
    } finally {
      setLoading(false);
    }
  };

  const imgSetter = (blob) => {
    setCroppedImageBlob(blob);
    var img = URL.createObjectURL(blob);
    setCroppedImg(img);
    setAvatar(img);
    setShowImgCropper(false);
  };

  const onImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      var imgFile = e.target.files[0];
      var imgURL = URL.createObjectURL(imgFile);
      setCroppedImg(imgURL);
      setAvatar(imgURL);
      setShowImgCropper(true);
    }
  };

  const hideImgCropper = () => {
    setShowImgCropper(false);
  };

  const handleBack = () => {
    navigate('/my-profile');
  };

  const handleUploadClick = () => {
    imgRef.current.click();
  };

  return (
    <>
      {/* Scrollable Body */}
      <div className="fixed top-24 bottom-20 left-0 right-0 overflow-y-auto">
        <div className="px-6 py-8">
          <div className="max-w-2xl mx-auto">
            {/* Back Button and Heading */}
            <div className="relative mb-2">
              {/* Back Button - Left Aligned */}
              <button
                onClick={handleBack}
                style={{ backgroundColor: colors?.primary || '#2B4B8C' }}
                className="absolute left-0 top-1/2 -translate-y-1/2 text-white p-1.5 rounded-lg hover:opacity-90 transition-opacity">
                <CaretLeftIcon size={16} />
              </button>
              {/* Heading - Centered */}
              <h1
                style={{ color: colors?.primary || '#2B4B8C' }}
                className="text-3xl font-bold text-center"
                dangerouslySetInnerHTML={{
                  __html: getTranslation(t, 'imageProfile.title', translationPrefix)
                }}
              />
            </div>

            <p className="text-gray-600 my-12 text-center text-lg max-w-md mx-auto">
              <span
                dangerouslySetInnerHTML={{
                  __html: getTranslation(t, 'imageProfile.description', translationPrefix)
                }}
              />
            </p>

            {/* Avatar Display */}
            {!showImgCropper && (
              <div className="flex flex-col items-center mb-8">
                <Avatar
                  className="w-64 h-64 mx-auto mb-8 overflow-hidden rounded-full"
                  img={avatar ? avatar : croppedImg}
                />

                {/* Upload Button */}
                <Button.ButtonRounded
                  onClick={handleUploadClick}
                  style={{ backgroundColor: colors?.primary || '#2B4B8C' }}
                  className="text-white font-semibold px-12 py-3 rounded-lg hover:opacity-90 transition-opacity w-full max-w-sm">
                  {getTranslation(t, 'imageProfile.upload', translationPrefix) ||
                    'Télécharger une image'}
                </Button.ButtonRounded>
              </div>
            )}

            {/* Image Cropper */}
            {showImgCropper && (
              <ImageCropPopup
                ref={cropperRef}
                imageUrl={avatar}
                onImageSet={imgSetter}
                turnOffCropper={hideImgCropper}
                orgPrefix={translationPrefix}
              />
            )}

            {/* Hidden File Input */}
            <div className="hidden">
              <input type="file" ref={imgRef} onChange={onImageChange} accept="image/*" />
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer with Save Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-300 px-6 py-6 z-40">
        <div className="max-w-2xl mx-auto flex justify-center">
          <Button.ButtonRounded
            onClick={buttonHandler}
            disabled={loading}
            bgColor="secondary"
            className="w-full max-w-xs py-3">
            <Spinner isSubmitting={loading} />
            {getTranslation(t, 'imageProfile.submit', translationPrefix)}
          </Button.ButtonRounded>
        </div>
      </div>
    </>
  );
}
